/**
 * 서버가 준 DL 배정 영역에 한글 제목을 붙여 렌더러 입력으로 만든다.
 *
 * 제목은 IXD 청크의 dlView 에만 있어 영역 응답에는 없다. 라이브 화면과 다운로드 뷰어가
 * 각자 조립하면 한쪽만 빠져도 배정 표시와 클릭 포커싱의 DL 제약이 조용히 갈리므로,
 * 조립을 여기 한 곳에 둔다.
 */

import type {
  DsdDlRegion,
  DsdLineItemRegion,
  DsdMemberRegion,
  IxdChunk,
} from "@/apis/compare/types";
import { buildTableLabelMap } from "@/utils/ixd-cell-model";

import type {
  DsdDlRegionView,
  DsdLineItemRegionView,
  DsdMemberRegionView,
} from "./dsd-preview-renderer";

export const buildDsdDlRegionViews = (
  regions: DsdDlRegion[] | null | undefined,
  ixdChunks: IxdChunk[] | null | undefined,
): DsdDlRegionView[] | null => {
  if (!regions?.length) return null;
  // `dlViews` 누락 방어 — 이 함수는 렌더 경로의 useMemo 에서 불린다. 이름표는 부수
  // 표시인데 여기서 throw 하면 비교 화면 전체가 흰 화면이 된다.
  //
  // id 없는 view 는 아예 넣지 않는다. `""` 로 뭉치면 id 없는 view 가 둘 이상일 때
  // 마지막 title 이 이기고, 무관한 DL 의 제목이 붙을 수 있다.
  //
  // `dlViewId` 만으로 조회하고 `region.chunkId` 는 안 쓴다 — DL view id 는 보고서 전역
  // 유일하다(서버 `dl_view_index` 도 `view_id` 하나로 키를 잡고, IXD 패널의
  // `[data-dl-id]` 조회도 전역이다).
  const titleById = new Map(
    (ixdChunks ?? []).flatMap((chunk) =>
      (chunk.dlViews ?? []).flatMap((view) =>
        view.id ? [[view.id, view.title] as const] : [],
      ),
    ),
  );
  return regions.map((region) => ({
    ...region,
    title: titleById.get(region.dlViewId) ?? null,
  }));
};

export const buildDsdLineItemRegionViews = (
  regions: DsdLineItemRegion[] | null | undefined,
  dlRegions: DsdDlRegion[] | null | undefined,
  ixdChunks: IxdChunk[] | null | undefined,
): DsdLineItemRegionView[] | null => {
  if (!regions?.length) return null;
  const labelsByDlViewId = _labelsByDlViewId(ixdChunks);
  const dlViewIdByDsdId = new Map(
    (dlRegions ?? []).flatMap((region) =>
      region.dsdIds.map((dsdId) => [dsdId, region.dlViewId] as const),
    ),
  );
  return regions.map((region) => {
    const dlViewId = region.dsdIds
      .map((dsdId) => dlViewIdByDsdId.get(dsdId))
      .find(Boolean);
    const labels = dlViewId ? labelsByDlViewId.get(dlViewId) : undefined;
    const [baseId] = region.lineItemId.split("/");
    return {
      ...region,
      title: labels?.get(region.lineItemId) ?? labels?.get(baseId) ?? null,
    };
  });
};

/**
 * dlViewId → 그 표의 태그 id → th 문구.
 *
 * **표 단위로 나눠 담는 것이 핵심이다.** 같은 태그의 문구가 표마다 다르다 — `.frm` 이
 * 표별 별칭(preferredLabel)을 지정하기 때문이고, 실제로 종근당 1Q 의
 * `dart_LongTermDepositsProvidedNet` 은 재무상태표에서 "보증금", 8번 주석에서
 * "비유동보증금" 이다. 전역 맵 하나로 뭉치면 먼저 만난 표(= 재무상태표)의 문구가
 * 이겨서, 주석 표를 보는 사용자에게 그 표에 없는 문구가 붙는다.
 *
 * 택소노미 표준명("장기보증금, 순액")을 쓰지 않는 이유도 같다 — 이름표는 **지금 보고
 * 있는 표의 행 문구**와 같아야 한다.
 */
const _labelsByDlViewId = (
  ixdChunks: IxdChunk[] | null | undefined,
): Map<string, Map<string, string>> => {
  const result = new Map<string, Map<string, string>>();
  for (const chunk of ixdChunks ?? []) {
    for (const view of chunk.dlViews ?? []) {
      if (!view.id || !view.table) continue;
      const labels = result.get(view.id) ?? new Map<string, string>();
      for (const rows of Object.values(view.table)) {
        for (const [id, label] of buildTableLabelMap(rows)) {
          if (!labels.has(id)) labels.set(id, label);
        }
      }
      result.set(view.id, labels);
    }
  }
  return result;
};

export const buildDsdMemberRegionViews = (
  regions: DsdMemberRegion[] | null | undefined,
  dlRegions: DsdDlRegion[] | null | undefined,
  ixdChunks: IxdChunk[] | null | undefined,
): DsdMemberRegionView[] | null => {
  if (!regions?.length) return null;
  const labelsByDlViewId = _labelsByDlViewId(ixdChunks);
  const dlViewIdByDsdId = new Map(
    (dlRegions ?? []).flatMap((region) =>
      region.dsdIds.map((dsdId) => [dsdId, region.dlViewId] as const),
    ),
  );
  const titleByDlViewId = new Map(
    (ixdChunks ?? []).flatMap((chunk) =>
      (chunk.dlViews ?? []).flatMap((view) =>
        view.id ? [[view.id, view.title] as const] : [],
      ),
    ),
  );
  return regions.map((region) => {
    // DL 은 payload 가 직접 준다. 비어 있으면 이 필드가 생기기 전에 저장된 결과라
    // line_item 과 같은 방식(dsdId → DL 영역)으로 되짚는다 — 안 그러면 그 표의 이름표가
    // 통째로 비고, 축 선택 키도 DL 별로 안 갈린다.
    const dlViewId =
      region.dlViewId ||
      region.dsdIds.map((dsdId) => dlViewIdByDsdId.get(dsdId)).find(Boolean) ||
      "";
    const labels = labelsByDlViewId.get(dlViewId);
    return {
      ...region,
      dlViewId,
      axisKey: `${dlViewId}::${region.axisId}`,
      dlTitle: titleByDlViewId.get(dlViewId) ?? null,
      title: labels?.get(region.memberId) ?? null,
    };
  });
};

/**
 * 검토 토글에 보여줄 **(DL, 축)** 목록.
 *
 * 축만으로 묶지 않는다 — 한 표에 여러 DL 이 걸리면 서로 다른 DL 이 같은 축 tag 를 쓸 수
 * 있고, 그때 member 집합도 색 배정도 DL 마다 다르다. 묶으면 두 DL 의 배정이 한 덩어리로
 * 칠해지고, tag id 를 모르는 축의 `axis-<순번>` 폴백은 아예 다른 축을 같은 키로 만든다.
 *
 * 축의 한글 이름은 어디에도 없다 — 서버는 tag id 만 주고, IXD 표의 th 는 축이 아니라
 * member 를 그린다. 그래서 **그 축의 member 이름표 두엇으로 축을 알아보게** 하고, DL 이
 * 둘 이상이면 DL 제목을 앞에 붙여 어느 표의 축인지 가른다.
 */
export const buildMemberAxisOptions = (
  regions: DsdMemberRegionView[] | null | undefined,
): { id: string; label: string }[] => {
  const byKey = new Map<
    string,
    { axisId: string; dlTitle: string | null; titles: string[] }
  >();
  for (const region of regions ?? []) {
    const entry = byKey.get(region.axisKey) ?? {
      axisId: region.axisId,
      dlTitle: region.dlTitle ?? null,
      titles: [],
    };
    if (region.title && !entry.titles.includes(region.title)) {
      entry.titles.push(region.title);
    }
    byKey.set(region.axisKey, entry);
  }
  const multipleDls =
    new Set([...byKey.values()].map((entry) => entry.dlTitle)).size > 1;
  return [...byKey].map(([id, entry]) => {
    const axisLabel = entry.titles.length
      ? entry.titles.slice(0, 2).join(" · ")
      : _axisTail(entry.axisId);
    return {
      id,
      label:
        multipleDls && entry.dlTitle
          ? `${entry.dlTitle} — ${axisLabel}`
          : axisLabel,
    };
  });
};

const _axisTail = (axisId: string): string =>
  axisId.split("_").pop()?.replace(/Axis$/, "") || axisId;
